import { isNapflixOSMode } from "./napflixOS";

// NapflixOS Back Manager v13
// Runtime back-action coordinator foundation.
let backState = {
  enabled: true,
  lastBackAt: null,
  lastHandledBy: null,
  backCount: 0,
  handlers: [],
};

const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) => listener(backState));
};

const normalizeHandler = (handler) => ({
  id: handler.id,
  priority: handler.priority ?? 0,
  enabled: handler.enabled !== false,
  canHandle: typeof handler.canHandle === "function"
    ? handler.canHandle
    : () => true,
  handle: typeof handler.handle === "function"
    ? handler.handle
    : () => false,
  label: handler.label || handler.id,
});

export function getBackState() {
  return backState;
}

export function setBackState(partial) {
  backState = {
    ...backState,
    ...partial,
  };

  notifyListeners();
  return backState;
}

export function subscribeBackState(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function registerBackHandler(handler) {
  if (!handler?.id) return getBackState();

  const nextHandler = normalizeHandler(handler);
  const handlers = [
    ...backState.handlers.filter((item) => item.id !== nextHandler.id),
    nextHandler,
  ];

  return setBackState({ handlers });
}

export function unregisterBackHandler(id) {
  if (!id) return getBackState();

  return setBackState({
    handlers: backState.handlers.filter((handler) => handler.id !== id),
  });
}

const findBackHandler = (reason) => {
  if (!backState.enabled) return false;

  return [...backState.handlers]
    .sort((a, b) => b.priority - a.priority)
    .find((candidate) => {
      if (!candidate.enabled) return false;

      try {
        return candidate.canHandle(reason, backState);
      } catch (err) {
        console.warn("NapflixOS back canHandle failed:", err);
        return false;
      }
    });
};

export function canHandleBack(reason = "back") {
  return !!findBackHandler(reason);
}

export function handleBack(reason = "back") {
  const handler = findBackHandler(reason);

  if (!handler) return false;

  try {
    const handled = handler.handle(reason, backState) !== false;

    if (handled) {
      setBackState({
        lastBackAt: Date.now(),
        lastHandledBy: handler.id,
        backCount: backState.backCount + 1,
      });
    }

    return handled;
  } catch (err) {
    console.warn("NapflixOS back handler failed:", err);
    return false;
  }
}

export function installBackRuntime() {
  if (typeof window === "undefined" || !isNapflixOSMode()) {
    return () => {};
  }

  const onKey = (event) => {
    if (event.key !== "Escape" && event.key !== "Backspace") return;
    if (!canHandleBack("global-keyboard")) return;

    const handled = handleBack("global-keyboard");

    if (!handled) return;

    event.preventDefault();
    event.stopImmediatePropagation?.() || event.stopPropagation();
  };

  window.addEventListener("keydown", onKey, true);

  return () => {
    window.removeEventListener("keydown", onKey, true);
  };
}
